/* global axios */
import ApiClient from './ApiClient';

// Shared AbortController for Kanban requests
let activeAbortController = null;

// Cancel any pending Kanban requests - exported as standalone function
export const cancelPendingKanbanRequests = () => {
  if (activeAbortController) {
    activeAbortController.abort();
    activeAbortController = null;
  }
};

class KanbanAPI extends ApiClient {
  constructor() {
    super('funnels', {
      accountScoped: true,
    });
  }

  // Alias for the exported function - uses static call
  // eslint-disable-next-line class-methods-use-this
  cancelPendingRequests() {
    cancelPendingKanbanRequests();
  }

  // Get stage conversations with pagination (optimized for Kanban)
  // Supports both simple filters (status, assigneeId, inboxId) and advanced filters (payload)
  getStageConversations(funnelId, options = {}) {
    // Cancel any previous pending request to prevent race conditions
    cancelPendingKanbanRequests();
    activeAbortController = new AbortController();

    const {
      perStage = 20,
      status,
      assigneeId,
      inboxId,
      sortBy,
      sortOrder,
      payload, // Advanced filters (same format as conversations/filter)
    } = options;

    const params = {
      per_stage: perStage,
    };

    if (status) params.status = status;
    if (assigneeId) params.assignee_id = assigneeId;
    if (inboxId) params.inbox_id = inboxId;
    if (sortBy) params.sort_by = sortBy;
    if (sortOrder) params.sort_order = sortOrder;

    const axiosConfig = {
      params,
      signal: activeAbortController.signal,
    };

    // If advanced filters are provided, use POST request
    if (payload && payload.length > 0) {
      return axios.post(
        `${this.url}/${funnelId}/kanban/stage_conversations`,
        payload,
        axiosConfig
      );
    }

    return axios.get(
      `${this.url}/${funnelId}/kanban/stage_conversations`,
      axiosConfig
    );
  }

  // Load more conversations for a specific stage
  loadMoreStage(funnelId, stageName, options = {}) {
    const { page = 2, perPage = 20 } = options;

    return axios.get(`${this.url}/${funnelId}/kanban/load_more_stage`, {
      params: {
        stage_name: stageName,
        page,
        per_page: perPage,
      },
    });
  }

  // Move conversation to a different stage
  moveConversation(funnelId, conversationId, stageName) {
    return axios.post(`${this.url}/${funnelId}/kanban/move_conversation`, {
      conversation_id: conversationId,
      stage_name: stageName,
    });
  }

  // Get conversations for a specific stage with pagination
  getConversations(funnelId, options = {}) {
    const {
      stageName,
      page = 1,
      perPage = 20,
      includeCounts = false,
      status,
      assigneeId,
    } = options;

    const params = {
      page,
      per_page: perPage,
    };

    if (stageName) params.stage_name = stageName;
    if (includeCounts) params.include_counts = 'true';
    if (status) params.status = status;
    if (assigneeId) params.assignee_id = assigneeId;

    return axios.get(`${this.url}/${funnelId}/kanban/conversations`, {
      params,
    });
  }
}

export default new KanbanAPI();
